import { yupResolver } from '@hookform/resolvers/yup';
import { Plus, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { memo, useEffect, useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { USER_ROLE } from 'src/constants/common';
import { setWorkflowSteps } from 'src/redux/auth.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import { editMember, inviteMember } from 'src/services/OrganizationService';
import { capitalizeFirstLetter } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { inviteMemberSchema } from './organization.const';

interface IProps {
    mode: string;
    team?: any;
    handleClose: (teamForm?: any, isSaveChanges?: boolean) => void;
    setIsRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
    updateMembersData: (data: any, isArray?: boolean) => void;
}

const userRoles = [
    {
        label: capitalizeFirstLetter(USER_ROLE.ADMIN?.toLowerCase()),
        value: USER_ROLE.ADMIN,
    },
    {
        label: capitalizeFirstLetter(USER_ROLE.USER?.toLocaleLowerCase()),
        value: USER_ROLE.USER,
    },
];

const InviteTeamMember = ({ mode, team, handleClose, updateMembersData }: IProps) => {
    //constants
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const workFlowSteps = useSelector((state: RootState) => state.auth.workFlowSteps);

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [isSaveChanges, setIsSaveChanges] = useState(false);

    const {
        handleSubmit,
        control,
        setError,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: yupResolver(inviteMemberSchema),
        defaultValues: {
            teamMembers: team ? [{ email: team?.email, role: team?.role?.toLowerCase() }] : [{ email: '', role: '' }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'teamMembers',
    });

    //functions

    const onSubmit = async (data: any) => {
        setIsLoading(true);
        setIsSaveChanges(false);

        try {
            if (mode === 'member-invite') {
                const payload = { teamMembers: data?.teamMembers };
                await inviteMember(payload)
                    .then((response: any) => {
                        if (response?.data?.status) {
                            updateMembersData(response?.data?.data?.createdMembers, true);
                            handleClose(data.teamMembers, true);
                            dispatch(setWorkflowSteps({ ...workFlowSteps, INVITE_TEAM_MEMBER: true }));
                            WalToast.success(t(data.teamMembers.length === 1 ? 'Organization.Invite sent' : 'Organization.Invites sent'));
                        }
                    })
                    .catch((error: any) => {
                        console.log('error :>> ', error);

                        if (error?.response?.data?.status === 'error') {
                            const existedMembers = error?.response?.data?.existedMembers;
                            // Track whether the failure was fully explained by per-field errors — when it was,
                            // the inline "User is already invited" messages are the only feedback shown, so the
                            // toast would just repeat what the form already says.
                            let hasFieldLevelError = false;

                            if (Array.isArray(existedMembers)) {
                                existedMembers.forEach((email: string) => {
                                    const index = data.teamMembers.findIndex((member: any) => member.email === email);

                                    if (index !== -1) {
                                        hasFieldLevelError = true;
                                        setError(`teamMembers.${index}.email`, {
                                            type: 'manual',
                                            message: `User is already invited`,
                                        });
                                    }
                                });
                            }

                            if (!hasFieldLevelError) {
                                WalToast.error(error?.response?.data?.message || error?.message);
                            }
                        } else {
                            WalToast.error(error?.message);
                        }
                    });
            } else {
                const payload = { role: data?.teamMembers[0]?.role };

                if (team?.id) {
                    await editMember(payload, team?.id)
                        .then((response: any) => {
                            if (response?.status) {
                                updateMembersData({ ...team, role: payload?.role });
                                handleClose(data.teamMembers, true);

                                WalToast.success(t('Organization.Member_updated'));
                            }
                        })
                        .catch((error: any) => {
                            console.log('error :>> ', error);

                            if (error?.response?.data?.status === 'error') {
                                WalToast.error(error?.response?.data?.message || error?.message);
                            }
                        });
                }
            }
            setIsLoading(false);
        } catch (error) {
            console.log('🚀 ~ onSubmit ~ error:', error);
            handleClose(data.teamMembers, true);
        }
    };

    const addTeamMember = () => {
        append({ email: '', role: '' });
    };

    //hooks

    useEffect(() => {
        addTeamMember();
    }, [mode === 'member-invite']);

    useEffect(() => {
        remove(1);
    }, [mode === 'member-update']);

    useEffect(() => {
        if (isSaveChanges) {
            handleSubmit(onSubmit)();
        }
    }, [isSaveChanges]);

    return (
        <CommonModal
            title={mode === 'member-invite' ? 'Invite Team Member' : 'Edit Team Member'}
            handleClose={() => handleClose()}
            secondaryBtnOnClick={handleClose}
            size={'max-w-[600px]'}
            primaryBtnText={mode === 'member-invite' ? t('Common.Invite') : t('Common.Save_Changes')}
            primaryBtnLoading={!isSaveChanges ? isLoading : false}
            primaryBtnDisabled={!isValid || isLoading || !isDirty}
            primaryBtnOnClick={handleSubmit(onSubmit)}
            secondaryBtnText="Cancel"
            secondaryBtnLoading={isSaveChanges ? isLoading : false}
        >
            <div className="px-4 py-4 space-y-3">
                <div className="space-y-3">
                    {fields.map((fieldItem, index) => (
                        <div key={fieldItem.id} className="flex gap-3">
                            <Controller
                                name={`teamMembers.${index}.email`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <InputText
                                        label={t('Common.Email')}
                                        labelClassName="block mb-1.5"
                                        inputName={field.name}
                                        placeholder={t('Common.Enter_Email')}
                                        value={field.value}
                                        onChangeFunc={(e) => field.onChange(e.target.value)}
                                        onBlur={field.onBlur}
                                        errorText={fieldState.error?.message}
                                        // Error rides on the alert icon inside the field, on hover, instead of as a line under it
                                        // (client, 2026-10-09). isIconError does BOTH halves of that: it renders the icon+tooltip and
                                        // it suppresses the ErrorMsg below, so the row keeps its height and "Add another" no longer
                                        // jumps down when a duplicate email comes back from the server.
                                        isIconError={!!fieldState.error?.message}
                                        required
                                        parentClassName="flex-1"
                                        disabled={mode === 'member-update'}
                                    />
                                )}
                            />

                            <Controller
                                name={`teamMembers.${index}.role`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <SelectBox
                                        name="role"
                                        label={t('Organization.Role')}
                                        labelClassName="form_label block mb-1.5"
                                        placeholder={t('Organization.Select_Role')}
                                        options={userRoles}
                                        value={userRoles.find((item) => item.value?.toLowerCase() === field.value?.toLowerCase()) ?? null}
                                        onChangeFunc={(item: any) => field.onChange(item?.value)}
                                        onBlur={field.onBlur}
                                        errorText={fieldState.error?.message ? true : false}
                                        parentClassName="flex-1"
                                        required
                                    />
                                )}
                            />

                            {mode === 'member-invite' && (
                                <div className="text-gray400 flex items-center justify-center mt-[22px] !h-[32px] !w-[32px]">
                                    <TooltipCmp message="Delete" childrenClassName="!flex">
                                        <Trash01
                                            className={`w-5 h-5 ${fields.length === 1 ? 'cursor-not-allowed text-grayLight200' : 'cursor-pointer text-grayLight600'}`}
                                            onClick={() => fields.length > 1 && remove(index)}
                                        />
                                    </TooltipCmp>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {mode === 'member-invite' && (
                    <ButtonCmp onClick={addTeamMember} className="btn-link-custom" icon={<Plus className="w-4 h-4" />}>
                        {t('Common.Add_another')}
                    </ButtonCmp>
                )}
            </div>
        </CommonModal>
    );
};

export default memo(InviteTeamMember);
